import React, { useState, useMemo } from "react";
import { useTranslation, Trans } from "react-i18next";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  Box,
  TableContainer,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
  ModalCloseButton,
  Spacer,
} from "@chakra-ui/react";
import {
  EquipmentType,
  EquipmentGroupType,
  EquipmentSupplierType,
  UniqueProcessType,
  PagePermissions,
} from '../../contexts/types';
import Decimal from 'decimal.js';
import { formatNumber } from '../../utils/utils';
import { getMultilingualDisplayName } from '../../utils/i18nDisplay';
import * as XLSX from "xlsx";
import '../common.css';

/**
 * モーダルのプロパティ
 */
interface Props {
  /** モーダルの表示/非表示 */
  isOpen: boolean;
  /** モーダルを閉じる関数 */
  onClose(): void;
  /**projectId */
  projectId: string;
  /**設備データ */
  equipmentListData: EquipmentType[];
  /**設備分類データ */
  equipmentGroups: EquipmentGroupType[];
  /**サプライヤーデータ */
  equipmentSuppliers: EquipmentSupplierType[];
  /**ユニークプロセスデータ */
  uniqueProcessData: UniqueProcessType[];
  /**金額計算関数 */
  calcAmount: (
    unitPrice: number | undefined,
    quantity: number | undefined,
    equipment: EquipmentType
  ) => Decimal | undefined;
  /**権限 */
  pagePermissions?: PagePermissions;
  /**編集ボタン押下時のコールバック */
  onEditClick?: () => void;
};

/**
 *設備一覧モーダル 
 */
const EquipmentList: React.FC<Props> = ({
  isOpen,
  onClose,
  projectId,
  equipmentListData,
  equipmentGroups,
  equipmentSuppliers,
  uniqueProcessData,
  calcAmount,
  pagePermissions,
  onEditClick,
}) => {

  const { t } = useTranslation();
  /**TITLE*/
  const TITLE = t('components.EquipmentListModal.title');

  /** 単価の小数点以下桁数 */
  const UNIT_PRICE_DECIMAL_PLACES = 2;
  /** トースト表示用 */
  const toast = useToast();
  /**EXCELダウンロードボタン活性制御 */
  const [isDownloading, setIsDownloading] = useState(false);

  /**
   * 編集ボタン押下時
   */
  const handleEditClick = () => {
    onClose(); // 一覧モーダルを閉じる
    onEditClick?.(); // 親から渡された編集モーダルを開くコールバックを呼ぶ
  };

  /**
   * Seq順で並び替え
   */
  const sortEquipmentListData = useMemo(() => {
    if (!equipmentListData || !uniqueProcessData) return [];

    return [...equipmentListData].sort((a, b) => {
      const aSeq =
        uniqueProcessData.find(p => p.uniqueProcessId === a.processId)?.uniqueProcessSeq ?? 9999;
      const bSeq =
        uniqueProcessData.find(p => p.uniqueProcessId === b.processId)?.uniqueProcessSeq ?? 9999;
      return aSeq - bSeq;
    });
  }, [equipmentListData, uniqueProcessData]);

  /**
   * ProcessCodeからItemName（工程名）を取得
   */
  const getItemNameByProcessId = (processId: string | undefined): string => {
    if (!processId || !uniqueProcessData) return "";
    const process = uniqueProcessData.find(p => p.uniqueProcessId === processId);
    return process?.uniqueProcessCycleTimeInSecondByPartNoList?.[0]?.itemName ?? "";
  };

  /**
   * 設備分類・サプライヤー・プロセスフィルター
   * @param data 一覧データ
   * @param targetId 設備に紐づくID
   * @param sort ユニークプロセス、設備グループ、サプライヤー
   * @returns IDに紐づく名前
   */
  const filterId = (data: EquipmentGroupType[] | EquipmentSupplierType[] | UniqueProcessType[], targetId: string, sort: "groups" | "suppliers" | "uniqueProcess") => {
    if (!data) return "";
    if (sort === "groups") {
      const groupArray = data as EquipmentGroupType[];
      const group = groupArray.find((i) => i.equipmentGroupId === targetId);
      if (!group) return "";
      return getMultilingualDisplayName(group.equipmentGroupName);
    } else if (sort === "suppliers") {
      const supplierArray = data as EquipmentSupplierType[];
      const supplier = supplierArray.find((i) => i.equipmentSupplierId === targetId);
      if (!supplier) return "";
      return getMultilingualDisplayName(supplier.equipmentSupplierName);
    } else if (sort === "uniqueProcess") {
      const uniqueProcessArray = data as UniqueProcessType[];
      return (uniqueProcessArray.find((i) => i.uniqueProcessId === targetId)?.uniqueProcessName || "");
    };
    return "";
  }

  /** 
   *  Excelダウンロード
   */
  // #region Excelダウンロード
  const downloadExcel = async () => {

    if (isDownloading) return;

    setIsDownloading(true)

    setTimeout(() => {
      setIsDownloading(false);
    }, 500);

    // ヘッダー設定
    const header = [
      "ProcessCode",
      "ItemNo",
      "工程名",
      "設備名",
      "単価",
      "通貨",
      "数量",
      "数量単位",
      "投資金額",
      "簿価",
      "償却年数",
      "申請予定月",
      "検収月",
      "償却開始月",
      "単純工",
      "多能工",
      "テクニシャン",
      "設備なし時に必要な人員数/perシフト",
      "設備分類",
      "サプライヤー",
    ];
    // シート情報初期化
    const sheetData: (string | number)[][] = [header];

    // processId ごとにグループ化
    const groupedData = sortEquipmentListData.reduce((acc, row) => {
      if (!acc[row.processId ?? ""])
        acc[row.processId ?? ""] = [];
      acc[row.processId ?? ""].push(row);
      return acc;
    }, {} as Record<string, EquipmentType[]>);

    // 見出し行と設備行を追加
    Object.keys(groupedData).forEach(processId => {
      // ProcessCode 見出し行
      sheetData.push([processId, ...Array(header.length - 1).fill("")]);

      groupedData[processId].forEach(row => {
        sheetData.push([
          "", // ProcessCode は空
          filterId(uniqueProcessData, row.processId ?? "", "uniqueProcess") ?? "",
          getItemNameByProcessId(row.processId ?? "") ?? "",
          row.equipmentName ?? "",
          row.unitPrice ?? "",
          row.currencyName ?? "",
          row.quantity ?? "",
          toSpreadsheetUnitLabel(row.quantityUnit),
          calcAmount(row.unitPrice, row.quantity, row)?.toNumber() ?? "",
          row.bookValue ?? 0,
          row.depreciationPeriod ?? "",
          row.applicationPlannedMonth ?? "",
          row.acceptanceMonth ?? "",
          row.depreciationStartMonth ?? "",
          row.unskilledWorker ?? "",
          row.multiSkilledWorker ?? "",
          row.technician ?? "",
          row.numberOfPeopleRequiredForWithoutEquipment ?? "",
          filterId(equipmentGroups, row.equipmentGroupId ?? "", "groups") ?? "",
          filterId(equipmentSuppliers, row.equipmentSupplierId ?? "", "suppliers") ?? "",
        ]);
      });
    });

    // 日付シートの作成
    const createDate = () => {
      const d = new Date(Date.now() + 9 * 60 * 60 * 1000);
      const f = (n: number) => String(n).padStart(2, "0");

      const today = `${d.getUTCFullYear()}-${f(d.getUTCMonth() + 1)}-${f(d.getUTCDate())} ` +
        `${f(d.getUTCHours())}:${f(d.getUTCMinutes())}:${f(d.getUTCSeconds())}`;
      return [[today]]
    }

    // セル幅計算
    const getAutoColWidths = (data: (string | number)[][]): XLSX.ColInfo[] => {
      const colWidths: number[] = [];

      for (const row of data) {
        row.forEach((cell, i) => {
          const cellValue = String(cell ?? "");
          const cellLength = [...cellValue].reduce(
            (sum, ch) => sum + (ch.charCodeAt(0) > 255 ? 2 : 1),
            0
          );
          colWidths[i] = Math.max(colWidths[i] || 10, cellLength);
        });
      }
      return colWidths.map(w => ({ wch: w + 2 }));
    };

    try {

      /** シート作成 */
      const wb = XLSX.utils.book_new();
      // 日付ページを１シート追加
      const downloadTimeData = createDate();
      const downloadTimeSheet = XLSX.utils.aoa_to_sheet(downloadTimeData);
      XLSX.utils.book_append_sheet(wb, downloadTimeSheet, "downloadTIme");

      // 設備一覧シートを１シート追加
      const ws = XLSX.utils.aoa_to_sheet(sheetData);
      ws['!cols'] = getAutoColWidths(sheetData);
      XLSX.utils.book_append_sheet(wb, ws, "EquipmentList");

      // ファイル名の日付を設定
      const today = new Date();
      const formatted = today.toISOString().split("T")[0].replace(/-/g, "")
      // ファイル保存
      const fileNamePrefix = t('components.EquipmentListModal.excel.fileNamePrefix');
      XLSX.writeFile(wb, `${fileNamePrefix}${projectId}_${formatted}.xlsx`);

      toast({
        title: TITLE,
        description: t('components.EquipmentListModal.message.excelExportSuccess'),
        status: "success",
        duration: 5000,
        isClosable: true,
      });

    } catch (error) {
      toast({
        title: TITLE,
        description: t('components.EquipmentListModal.message.excelExportFailed'),
        status: "error",
        duration: 5000,
        isClosable: true,
      })
      console.error(`ERROR:`, error);
    }
  };

  // 固定選択肢のコードと多言語キーの対応
  const QUANTITY_UNIT_KEYS = [
    { code: "unit", key: "components.EquipmentEditModal.options.quantityUnit.unit" },
    { code: "set",  key: "components.EquipmentEditModal.options.quantityUnit.set" },
    { code: "pcs",  key: "components.EquipmentEditModal.options.quantityUnit.pcs" },
  ] as const;

  /**
   * 【出力用】DB保存値（unit/set/pcs または自由入力）を表示名に変換
   */
  const toSpreadsheetUnitLabel = (code?: string | null): string => {
    const targetCode = code || "unit"; // 初期値
    const matched = QUANTITY_UNIT_KEYS.find(item => item.code === targetCode);
    // 固定選択肢に一致すれば翻訳テキスト（例: "台"）、なければ自由入力値をそのまま返す
    return matched ? t(matched.key) : targetCode;
  };

  //#endregion
  /**
   * レイアウト 
   */
  // #region　レイアウト
  return (
    <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxW="1783px" minW="720px" maxH="90vh">
        <ModalHeader>{t('components.EquipmentListModal.title')}</ModalHeader>
        {/* フォーカス制御用ダミー */}
        <div
          tabIndex={0}
          style={{ outline: "none" }}
        />
        <ModalCloseButton />
        <ModalBody pb={6} overflow="hidden">
          <Text display={"flex"} justifyContent={"flex-start"} ml={5}>
            {t('components.EquipmentListModal.label.equipmentCount')}: {equipmentListData?.length ?? 0} {t('components.EquipmentListModal.label.count')}
          </Text>
          <Box display="flex" justifyContent="flex-start" h={"calc(90vh - 220px)"} mt="10px" px="10px">
            <Box width="100%">
              <TableContainer height="100%" overflowY="auto" overflowX="auto" border="1px solid #ccc">
                <Table variant="simple" size="sm" style={{ tableLayout: "fixed", borderCollapse: "separate" }} cellSpacing={0} className="mm-ceas-table-extend">
                  <colgroup>
                    <col width="200px" />
                    <col width="270px" />
                    <col width="200px" />
                    <col width="185px" />
                    <col width="250px" />
                    <col width="120px" />
                    <col width="120px" />
                    <col width="120px" />
                    <col width="250px" />
                    <col width="250px" />
                    <col width="100px" />
                    <col width="185px" />
                    <col width="185px" />
                    <col width="185px" />
                    <col width="115px" />
                    <col width="115px" />
                    <col width="115px" />
                    <col width="110px" />
                    <col width="200px" />
                    <col width="250px" />
                  </colgroup>
                  <Thead position="sticky" top={0} zIndex={10}>
                    <Tr>
                      <Th rowSpan={2}>ProcessCode</Th>
                      <Th rowSpan={2}>ItemNo</Th>
                      <Th rowSpan={2}>{t('components.EquipmentListModal.tableHeader.itemName')}</Th>
                      <Th rowSpan={2}>{t('components.EquipmentListModal.tableHeader.equipmentName')}</Th>
                      <Th rowSpan={2}>{t('components.EquipmentListModal.tableHeader.unitPrice')}</Th>
                      <Th rowSpan={2}>{t('components.EquipmentListModal.tableHeader.currency')}</Th>
                      <Th rowSpan={2}>{t('components.EquipmentListModal.tableHeader.quantity')}</Th>
                      <Th rowSpan={2}>{t('components.EquipmentListModal.tableHeader.quantityUnit')}</Th>
                      <Th rowSpan={2}>{t('components.EquipmentListModal.tableHeader.investmentAmount')}</Th>
                      <Th rowSpan={2}>{t('components.EquipmentListModal.tableHeader.bookValue')}</Th>
                      <Th rowSpan={2}>{t('components.EquipmentListModal.tableHeader.depreciationPeriod')}</Th>
                      <Th rowSpan={2}>{t('components.EquipmentListModal.tableHeader.applicationPlannedMonth')}</Th>
                      <Th rowSpan={2}>{t('components.EquipmentListModal.tableHeader.acceptanceMonth')}</Th>
                      <Th rowSpan={2}>{t('components.EquipmentListModal.tableHeader.depreciationStartMonth')}</Th>
                      <Th rowSpan={1} colSpan={3}><Text fontSize="12px">{t('components.EquipmentListModal.tableHeader.staffRequired')}</Text></Th>
                      <Th rowSpan={2}><Text fontSize="12px"><Trans i18nKey="components.EquipmentListModal.tableHeader.staffWithoutEquipment" components={[<br key="0" />]} /></Text></Th>
                      <Th rowSpan={2}>{t('components.EquipmentListModal.tableHeader.mainMachinery')}</Th>
                      <Th rowSpan={2}>{t('components.EquipmentListModal.tableHeader.supplier')}</Th>
                    </Tr>
                    <Tr>
                      <Th><Text fontSize="12px">{t('components.EquipmentListModal.tableHeader.unskilledWorker')}</Text></Th>
                      <Th><Text fontSize="12px">{t('components.EquipmentListModal.tableHeader.multiSkilledWorker')}</Text></Th>
                      <Th><Text fontSize="12px">{t('components.EquipmentListModal.tableHeader.technician')}</Text></Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {sortEquipmentListData.map((row, idx) => {
                      return (
                        <Tr key={idx}>
                          <Td borderRight="1px solid" borderColor="gray.200">
                            {/* processCode */}
                            <Text isTruncated={true} title={row.processId ?? ""}>
                              {row.processId ?? ""}
                            </Text>
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200">
                            {/* ItemNo */}
                            <Text isTruncated={true} title={filterId(uniqueProcessData, row.processId ?? "", "uniqueProcess")}>
                              {filterId(uniqueProcessData, row.processId ?? "", "uniqueProcess")}
                            </Text>
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200">
                            {/* 工程名 */}
                            <Text isTruncated={true} title={getItemNameByProcessId(row.processId ?? "")}>
                              {getItemNameByProcessId(row.processId ?? "")}
                            </Text>
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200">
                            {/* 設備名 */}
                            <Text isTruncated={true} title={row.equipmentName ?? ""}>
                              {row.equipmentName ?? ""}
                            </Text>
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200">
                            {/* 単価 */}
                            <Text isTruncated={true} >
                              {formatNumber(row.unitPrice || 0, row.currencyName ?? "", UNIT_PRICE_DECIMAL_PLACES)}
                            </Text>
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200">
                            {/* 通貨 */}
                            <Text isTruncated={true}>
                              {row.currencyName ?? ""}
                            </Text>
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200">
                            {/* 数量（トータル数量がある場合はトータル数量を表示） */}
                            <Text isTruncated={true} >
                              {row.quantity ?? ""}
                            </Text>
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200">
                            {/* 数量単位 */}
                            <Text isTruncated={true} >
                              {toSpreadsheetUnitLabel(row.quantityUnit)}
                            </Text>
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200">
                            {/* 投資金額 */}
                            <Text isTruncated={true} >
                              {row ?
                                formatNumber(
                                  calcAmount(row.unitPrice, row.quantity, row)
                                  || 0, row.currencyName ?? ""
                                ) : ""
                              }
                            </Text>
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200">
                            {/* 簿価 */}
                            <Text isTruncated={true} >
                              {formatNumber(new Decimal(row.bookValue || 0), row.currencyName ?? "")}
                            </Text>
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200">
                            {/* 償却年数 */}
                            <Text isTruncated={true} >
                              {row.depreciationPeriod ?? ""}
                            </Text>
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200">
                            {/* 申請予定月 */}
                            <Text isTruncated={true}>
                              {row.applicationPlannedMonth ?? ""}
                            </Text>
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200">
                            {/* 検収月 */}
                            <Text isTruncated={true}>
                              {row.acceptanceMonth ?? ""}
                            </Text>
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200">
                            {/* 償却開始月 */}
                            <Text isTruncated={true}>
                              {row.depreciationStartMonth ?? ""}
                            </Text>
                          </Td>

                          <Td borderRight="1px solid" borderColor="gray.200">
                            {/* 設備に必要な人員数 - 単純工 */}
                            <Text isTruncated={true}>
                              {row.unskilledWorker ?? ""}
                            </Text>
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200">
                            {/* 設備に必要な人員数 - 多能工 */}
                            <Text isTruncated={true}>
                              {row.multiSkilledWorker ?? ""}
                            </Text>
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200">
                            {/* 設備に必要な人員数 - テクニシャン */}
                            <Text isTruncated={true}>
                              {row.technician ?? ""}
                            </Text>
                          </Td>

                          <Td borderRight="1px solid" borderColor="gray.200">
                            {/* 設備なし時に必要な人員数/PERシフト */}
                            <Text isTruncated={true}>
                              {row.numberOfPeopleRequiredForWithoutEquipment ?? ""}
                            </Text>
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200">
                            {/* 設備分類 */}
                            <Text isTruncated={true} title={filterId(equipmentGroups, row.equipmentGroupId ?? "", "groups") ?? ""}>
                              {filterId(equipmentGroups, row.equipmentGroupId ?? "", "groups") ?? ""}
                              {equipmentGroups.find(g => g.equipmentGroupId === row.equipmentGroupId)?.isCandidate && (
                                <Box as="span" color="orange.500" fontSize="xs" ml={1}>({t('common.label.candidate')})</Box>
                              )}
                            </Text>
                          </Td>
                          <Td borderRight="1px solid" borderColor="gray.200">
                            {/* 設備サプライヤー */}
                            <Text isTruncated={true} title={filterId(equipmentSuppliers, row.equipmentSupplierId ?? "", "suppliers") ?? ""}>
                              {filterId(equipmentSuppliers, row.equipmentSupplierId ?? "", "suppliers") ?? ""}
                              {equipmentSuppliers.find(s => s.equipmentSupplierId === row.equipmentSupplierId)?.isCandidate && (
                                <Box as="span" color="orange.500" fontSize="xs" ml={1}>({t('common.label.candidate')})</Box>
                              )}
                            </Text>
                          </Td>
                        </Tr>
                      )
                    })}
                  </Tbody>
                </Table>
              </TableContainer>
            </Box>
          </Box>
        </ModalBody>
        <ModalFooter>
          <Button
            onClick={handleEditClick}
            colorScheme="blue"
            isDisabled={!pagePermissions?.isEditable}
            mr={2}
          >
            {t('common.button.edit')}
          </Button>
          <Button
            onClick={downloadExcel}
            colorScheme="green"
            isDisabled={!equipmentListData || equipmentListData.length === 0}
            isLoading={isDownloading}
          >
            {t('common.button.excelExport')}
          </Button>
          <Spacer />
          <Button ml={4} onClick={onClose}>{t('components.EquipmentListModal.button.close')}</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
  //#endregion
};

export default EquipmentList;
